/**
 * Wires `galleryAgents.model.ts` to its two reads and its one write.
 *
 * Calls `fetchGalleryAgentRoster` for the buyer half and `fetchGalleryInventory`
 * for the seller half — the unanswered offers, which need no read of their
 * own — then `signWorkThumbnails` in a second pass so a slow signing never
 * holds up the list. The one write is `endAgentMandate`, recalling a work,
 * followed by a re-read.
 *
 * The state the model cannot hold is the chosen side, the set of folded
 * groups, which mandate is mid-flight, and the reload counter.
 */

import { useCallback, useEffect, useState } from "react";
import type { ConsignedWork, GalleryAgentPlacement } from "@ondo/shared";

import { endAgentMandate, fetchGalleryAgentRoster, fetchGalleryInventory } from "../lib/api.ts";
import { signWorkThumbnails } from "../lib/workImages.ts";
import { closedKey, describeAgentsError, toGalleryAgentsView } from "./galleryAgents.model.ts";
import type { AgentSide, GalleryAgentsView } from "./galleryAgents.model.ts";

export interface GalleryAgentsController {
  view: GalleryAgentsView;
  /** Switch between the two directions. A filter, not a route. */
  chooseSide: (side: AgentSide) => void;
  /** Fold one counterparty's works away. */
  toggleGroup: (key: string) => void;
  /** Recall a work from an agent. Ends the mandate; the row is never deleted. */
  recall: (workId: string, mandateId: string) => void;
  /** Which mandate is mid-flight, so its control can be disabled. */
  recalling: string | null;
}

/**
 * The two directions of "agent", from two reads and one write.
 *
 * THE SELLER HALF NEEDS NO READ OF ITS OWN. `ondo.gallery_inventory()` already
 * returns every live consignment with `consignorOndoId` and `consignorName` on
 * it, so grouping the unanswered ones by whoever sent them is arithmetic over a
 * list Inventory was fetching anyway. That is the whole of the seller tab.
 *
 * ONE WRITE, AND IT IS THE NARROW ONE. Recalling a work ends a mandate, which is
 * a decision about a row and belongs on the row. Appointing an agent is not
 * here: it needs the work, its ONDO code, an eligibility check on blur and a
 * counterparty, and it already exists on the work's own record — a second door
 * onto `POST /v1/works/:id/agents` would be a second opinion about an
 * authorisation the database decides. The card links there instead.
 *
 * ANSWERING AN OFFER IS NOT HERE EITHER, and the copy says so. Accept and
 * Decline live on Inventory because the terms live there — asking a gallery to
 * commit to holding somebody's property from a screen that shows no asking price
 * would be the interface hiding the decision it is asking for.
 *
 * @param workBase '/gallery/ONDO-6624/work' — where a row links to.
 */
export function useGalleryAgentsController(workBase: string): GalleryAgentsController {
  const [placements, setPlacements] = useState<GalleryAgentPlacement[] | null>(null);
  const [offers, setOffers] = useState<ConsignedWork[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState<Map<string, string>>(new Map());
  const [side, setSide] = useState<AgentSide>("buyer");
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const [recalling, setRecalling] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let active = true;

    Promise.all([fetchGalleryAgentRoster(), fetchGalleryInventory()])
      .then(([roster, inventory]) => {
        if (!active) return;
        // Unanswered offers only. An accepted consignment is inventory, and it
        // is Inventory's screen — this one is about who is bringing work in.
        const open = inventory.filter((work) => work.acceptedAt === null);
        setPlacements(roster);
        setOffers(open);
        setError(null);
        return signWorkThumbnails([
          ...roster.map((row) => ({ id: row.workId, imagePath: row.imagePath })),
          ...open.map((work) => ({ id: work.workId, imagePath: work.imagePath })),
        ]);
      })
      .then((signed) => {
        // A second pass, so a slow signing round-trip never holds up the list.
        if (active && signed) setImages(signed);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setPlacements([]);
        setOffers([]);
        setError(describeAgentsError(cause));
      });

    return () => {
      active = false;
    };
  }, [reloads]);

  const toggleGroup = useCallback((key: string) => {
    setClosed((previous) => {
      const next = new Set(previous);
      const marker = closedKey(key);
      if (next.has(marker)) next.delete(marker);
      else next.add(marker);
      return next;
    });
  }, []);

  const recall = useCallback((workId: string, mandateId: string) => {
    setRecalling(mandateId);
    endAgentMandate(workId, mandateId)
      .then(() => setReloads((n) => n + 1))
      .catch((cause: unknown) => setError(describeAgentsError(cause)))
      .finally(() => setRecalling(null));
  }, []);

  return {
    view: error
      ? { kind: "error", message: error }
      : toGalleryAgentsView({ placements, offers, images, side, openKeys: closed, workBase }),
    chooseSide: setSide,
    toggleGroup,
    recall,
    recalling,
  };
}
